import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, Search } from "lucide-react";
import type { VenueSummary } from "../hooks/useVenueList";
import PrimaryButton from "../buttons/primaryButton";
import { CTA_FULL_WIDTH } from "../buttons/ctaClassNames";

interface VenueSelectorProps {
  readonly venues: VenueSummary[];
  readonly loading: boolean;
  readonly error?: string | null;
  readonly onRetry?: () => void;
  readonly selectedVenueId?: string;
  readonly onSelect: (venueId: string) => void;
  readonly searchEnabled?: boolean;
}

/** Format a venue address from its parts. */
function formatAddress(venue: VenueSummary): string {
  const parts: string[] = [];
  if (venue.street) parts.push(venue.street);
  if (venue.city) parts.push(venue.city);
  if (venue.postcode) parts.push(venue.postcode);
  return parts.join(", ");
}

/**
 * Card-based venue picker for multi-venue bookings.
 *
 * Renders a list of venue cards with name and address. When `searchEnabled`
 * is true, a search input filters venues by name or address.
 */
const VenueSelector: React.FC<VenueSelectorProps> = ({
  venues,
  loading,
  error,
  onRetry,
  selectedVenueId,
  onSelect,
  searchEnabled = false,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const filteredVenues = useMemo(() => {
    if (!searchQuery.trim()) return venues;
    const query = searchQuery.toLowerCase();
    return venues.filter((venue) => {
      const address = formatAddress(venue).toLowerCase();
      return venue.title.toLowerCase().includes(query) || address.includes(query);
    });
  }, [venues, searchQuery]);

  if (loading) {
    return (
      <div className="gcw:flex gcw:flex-col gcw:gap-bs-3">
        <output className="gcw:text-text-muted gcw:text-center gcw:py-6" aria-live="polite">
          {t("booking_venues_loading")}
        </output>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gcw:flex gcw:flex-col gcw:gap-bs-3" role="alert">
        <p className="gcw:text-error gcw:text-center gcw:py-4">{t("booking_venues_error")}</p>
        {onRetry && (
          <PrimaryButton
            text={t("booking_try_again")}
            type="button"
            onClick={onRetry}
            className={`gcw:inline-flex gcw:items-center gcw:justify-center gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 pay-button ${CTA_FULL_WIDTH}`}
          />
        )}
      </div>
    );
  }

  return (
    <div className="gcw:flex gcw:flex-col gcw:gap-bs-3">
      {searchEnabled && (
        <div className="gcw:relative">
          <Search
            size={18}
            className="gcw:absolute gcw:left-3 gcw:top-1/2 gcw:-translate-y-1/2 gcw:text-text-muted gcw:pointer-events-none"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("booking_venue_search_placeholder")}
            className="gcw:w-full gcw:rounded-md gcw:border gcw:border-light-grey gcw:py-2.5 gcw:pl-10 gcw:pr-3 gcw:text-body gcw:text-text-primary gcw:placeholder:text-text-muted gcw:focus:border-primary gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-1"
            aria-label={t("booking_venue_search_placeholder")}
          />
        </div>
      )}

      <div
        id="gc-booking-venue-list"
        className="gcw:flex gcw:flex-col gcw:border gcw:border-light-grey gcw:rounded-md gcw:overflow-hidden"
        aria-label={t("booking_select_venue_title")}
      >
        {filteredVenues.map((venue) => {
          const address = formatAddress(venue);
          const isSelected = venue._id === selectedVenueId;

          return (
            <button
              key={venue._id}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelect(venue._id)}
              className={`gcw:flex gcw:items-center gcw:justify-between gcw:gap-2 gcw:px-4 gcw:py-3 gcw:text-left gcw:border-b gcw:border-light-grey gcw:last:border-b-0 gcw:bg-white gcw:hover:bg-lighter-grey gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-inset gcw:text-text-primary ${
                isSelected ? "gcw:bg-lighter-grey" : ""
              }`}
              aria-label={venue.title}
            >
              <div className="gcw:flex gcw:flex-col gcw:gap-0.5 gcw:min-w-0">
                <span className="gcw:font-medium gcw:text-body gcw:line-clamp-3 gcw:text-ellipsis">{venue.title}</span>
                {address && (
                  <span className="gcw:text-sm gcw:text-text-muted gcw:line-clamp-3 gcw:text-ellipsis">{address}</span>
                )}
              </div>
              <ChevronRight size={18} className="gcw:text-text-muted gcw:shrink-0" aria-hidden="true" />
            </button>
          );
        })}
      </div>

      {searchQuery && filteredVenues.length === 0 && (
        <output className="gcw:text-text-muted gcw:text-center gcw:py-4 gcw:block" aria-live="polite">
          {t("booking_venue_search_no_results")}
        </output>
      )}
    </div>
  );
};

export default VenueSelector;
